// Fog volumes of a map (R_LoadFogs), the fog image (R_CreateFogImage) and
// the fog texture coordinates (RB_CalcFogTexCoords), without Three.js (the
// map loading worker uses them); fog.ts draws them.

import type { Bsp } from '../shared/bsp';
import type { Entity } from '../shared/entities';
import type { ShaderDef } from './shaderScripts';

export interface FogVolume {
  shader: string;
  /** fogparms colour 0..1. */
  color: [number, number, number];
  /** 1 / depthForOpaque */
  tcScale: number;
  /** ET's fog->surface: the visible side's plane flipped (normal, dist) - null: the eye is always inside. */
  surface: [number, number, number, number] | null;
  global: boolean;
}

export interface MapFogs {
  /** By the BSP's fog index (surface fogNum). */
  volumes: (FogVolume | null)[];
  /** The global fog, if any. */
  global: FogVolume | null;
}

export const FOG_S = 16;
export const FOG_T = 16;

/** R_LoadFogs: fog volumes with their colour, thickness and gradient plane. */
export function loadFogs(bsp: Bsp, shaders: Map<string, ShaderDef>, worldspawn: Entity | undefined): MapFogs {
  const make = (shader: string, brushNum: number, visibleSide: number): FogVolume | null => {
    const parms = shaders.get(shader.toLowerCase())?.fogParms;
    if (!parms) return null;
    let surface: FogVolume['surface'] = null;
    const brush = brushNum >= 0 ? bsp.brushes[brushNum] : undefined;
    if (brush && visibleSide >= 0 && visibleSide < brush.numSides) {
      const plane = bsp.planes[bsp.brushSides[brush.firstSide + visibleSide]?.planeNum ?? -1];
      if (plane) surface = [-plane.normal[0], -plane.normal[1], -plane.normal[2], -plane.dist];
    }
    return { shader, color: [...parms.color], tcScale: 1 / parms.depthForOpaque, surface, global: brushNum === -1 };
  };
  const volumes = bsp.fogs.map((f) => make(f.shader, f.brushNum, f.visibleSide));
  let global = volumes.find((v) => v?.global) ?? null;
  // old compilers: the worldspawn key names the global fog shader
  const key = worldspawn?._fog ?? worldspawn?.fog;
  if (!global && key) global = make(key, -1, -1);
  return { volumes, global };
}

/** R_CreateFogImage (ydnar's linear version): alpha = 270 * s * t, the first column clear, the last opaque. */
export function fogImageData(): Uint8Array {
  const data = new Uint8Array(FOG_S * FOG_T * 4);
  for (let x = 0; x < FOG_S; x++) {
    for (let y = 0; y < FOG_T; y++) {
      let alpha = Math.trunc(270 * (x / FOG_S) * (y / FOG_T));
      alpha = Math.max(0, Math.min(255, alpha));
      if (x === 0) alpha = 0;
      else if (x === FOG_S - 1) alpha = 255;
      const o = (y * FOG_S + x) * 4;
      data[o] = data[o + 1] = data[o + 2] = 255;
      data[o + 3] = alpha;
    }
  }
  return data;
}

/** RB_CalcFogTexCoords for one point (tests and CPU checks; the shader does the same per vertex). */
export function fogTexCoords(fog: FogVolume, point: ArrayLike<number>, view: ArrayLike<number>, forward: ArrayLike<number>): [number, number] {
  const s = ((point[0] - view[0]) * forward[0] + (point[1] - view[1]) * forward[1] + (point[2] - view[2]) * forward[2]) * fog.tcScale;
  if (!fog.surface) return [s, 1];
  const [nx, ny, nz, d] = fog.surface;
  // fogDepthVector = (surface.xyz, -surface.w) * tcScale
  const depth = (p: ArrayLike<number>) => (p[0] * nx + p[1] * ny + p[2] * nz - d) * fog.tcScale;
  let t = depth(point);
  const eyeT = depth(view);
  if (eyeT >= 0) t += eyeT;
  return [s, t];
}

